import * as THREE from 'three';
import { frameOrbit } from '../scene/cameras.js';
import { content } from '../scene/context.js';
import { rebuildHelpers } from '../scene/helpers.js';
import { applyLighting, setShadowRes, setShadowSoft } from '../scene/lighting.js';
import { applyAnimation, snapKeysToFrames } from '../state/timeline.js';
import { parentToNewNull, syncTransform, addObject, applyLook, disposeNode, refitRecess, rescaleForScreen, syncNode, syncParenting } from '../scene/objects.js';
import { reapVideoReaders } from '../scene/video.js';
import { fitStage } from '../scene/renderer.js';
import { resetHistory } from '../state/history.js';
import { $, S } from '../state/store.js';
import { emit, on } from '../state/bus.js';
import { buildHandles, cimg, setChip, setCornersLocked, wrap } from '../ui/calibrate-view.js';
import { embedPhoto, goStage, noteScreenBaseline, syncCornerWidth } from '../ui/rails.js';
import { toast } from '../ui/toast.js';
import {
  DOC_VERSION, LEGACY_VERSION, assetIdsIn, dehydrate, fromDoc, hasUnuploadedAssets,
  hydrate, nextIdFor, toDoc, toV2
} from '../core/doc.js';
import { isConfigured, requireClient } from './supabase.js';
import { currentUser, isSignedIn } from './auth.js';
import { linkToProject, signedUrls, upload, uploadDataUrl } from './assets.js';
import { getBlob } from './media-store.js';
import { canCreateProject, canSaveToCloud } from './limits.js';
import { registerDocFonts } from './fonts.js';
import {
  deleteLocal, duplicateLocal, getLocal, listLocal, overwriteLocal, renameLocal,
  safeFileName, saveLocal
} from './projects-fs.js';
import { claimLock, isShared, lockHolder, releaseLock } from './sharing.js';
import { clearSiteClip } from '../export/site-clip.js';

/* Opening a project — from a local file or from the database — is one operation
   with two front doors. It used to live entirely inside the #fileProj change
   handler, which is why the cloud version needed it pulled out rather than
   copied: two apply paths would drift, and the one that drifts is always the one
   you are not looking at. */

/* ── apply ───────────────────────────────────────────────────────────────── */

/** Put a validated document into the live scene. */
export function applyDoc(d) {
  /* The filmed video behind an on-site render belongs to the project it was loaded for and is not saved
     with it, so it must not follow the person into another one. */
  clearSiteClip();
  S.objects.forEach(o => { if(o.node.parent) o.node.parent.remove(o.node); disposeNode(o.node); });
  /* The faces themselves are registered by registerDocFonts BEFORE this runs —
     applyDoc is synchronous and syncNode traces immediately, so a face that is
     not in document.fonts by now would be traced as a fallback. */
  S.fonts = (d.fonts || []).map(f => ({ ...f }));
  Object.assign(S, {
    screen: d.screen, res: d.res, corners: d.corners, cornerPanel: d.cornerPanel,
    /* Already normalised and made exclusive of cornerPanel by doc.js's
       migrate, so this is a straight assignment like the rest. */
    screenArray: d.screenArray,
    nudge: d.nudge, solved: d.solved, seeded: true,
    bg: d.bg, lighting: d.lighting, lightAz: d.lightAz, lightEl: d.lightEl,
    tone: d.tone, envInt: d.envInt, aoStrength: d.aoStrength, aoReach: d.aoReach, dofStrength: d.dofStrength, dofFocus: d.dofFocus, ss: d.ss, motionBlur: d.motionBlur, gizmoSize: d.gizmoSize,
    showBezels: d.showBezels, bezelOpacity: d.bezelOpacity, bezelPhoto: d.bezelPhoto, onsite: { ...d.onsite },
    timeline: d.timeline, playhead: 0,
    objects: [], selId: null
  });
  // Without this, editing the screen size in a freshly-opened project would
  // compare it against whatever the PREVIOUS project's screen happened to be
  // (ui/rail-right.js's onScreenChange remembers the last size it saw) and
  // proportionally rescale every object here for a reason that has nothing
  // to do with this project at all.
  noteScreenBaseline();
  /* The second panel's width in metres is derived (ui/rail-right.js), so the
     copy in the document is a cache, and a cache saved against a different
     screen size than the one just loaded would be wrong. Recomputed rather
     than trusted — it costs one divide and it cannot then go stale. */
  syncCornerWidth();
  S.focal.mode = d.focal.mode;
  S.focal.manualMM = d.focal.manualMM;
  S.eye = d.eye ? new THREE.Vector3(d.eye.x, d.eye.y, d.eye.z) : null;

  if (d.photo?.url) {
    S.photo = d.photo;
    cimg.src = d.photo.url;
    $('#siteImg').src = d.photo.url;
    setCornersLocked(!!d.cornersLocked);
    $('#drop').classList.add('hidden');
    wrap.classList.remove('hidden');
    buildHandles();
  } else {
    /* A document with no photo has to CLEAR whichever one is on screen, not
       inherit it. This used to only ever set a photo, never unset one — so
       opening a project saved without its photo (see the "Include the
       reference photo" option, which writes exactly that) while another
       project's photo was loaded left the old image sitting under the new
       project's calibration, reading as though the two belonged together.
       goStage below re-syncs the Calibrate view against S.photo and moves
       On-site to Split when there is none, so only the image itself and the
       corner lock need undoing here. */
    S.photo = null;
    cimg.removeAttribute('src');
    $('#siteImg').removeAttribute('src');
    setCornersLocked(false);
    wrap.classList.add('hidden');
  }

  d.objects.forEach(o => { o.node = null; S.objects.push(o); syncNode(o); });
  /* Same order-independence fix-up as state/history.js's applySnapshot: a
     child's own syncNode above can run before its parent's, so it lands on
     content until this second pass reaches it. Also drops a parentId that
     doesn't resolve within this document. */
  S.objects.forEach(o => {
    if(o.parentId != null && !S.objects.some(p=>p.id===o.parentId)) o.parentId = null;
  });
  S.objects.forEach(syncParenting);
  S.nextId = nextIdFor(S.objects);

  /* WHAT A LOADED PROJECT UNLOCKS, and it is not all-or-nothing.

     This gated all three on S.solved and had no else, which was wrong twice
     over. A project saved with a stale angle — corners nudged after locking,
     which sets solved false (ui/calibrate-view.js's markStale) — reopened with
     Design, Motion and Export all greyed out and NOTHING that could re-enable
     them: opening it again ran this same branch to the same end. And because
     the else was missing, opening a good project afterwards left them enabled
     for every project loaded after it, broken ones included. Reported exactly
     that way: reopening the same project does not help, opening another one
     does.

     The split now follows the rule markStale itself already applies in-session
     — a stale angle stops the EXPORT, because the deliverable is rendered from
     that viewpoint, and leaves Design and Motion alone, because the scene is
     perfectly workable while the viewpoint is being re-derived. Those two
     follow whether a viewpoint exists at all.

     Both sides assign. That is the half that stops one project's state being
     answered by the previous project's. */
  const hasView = !!S.eye;
  $('.step[data-stage=compose]').disabled = !hasView;
  $('.step[data-stage=animate]').disabled = !hasView;
  $('#btnExport').disabled = !hasView || !S.solved;
  if (!hasView) setChip('', 'Not calibrated');
  else if (S.solved) setChip('ok', 'Angle locked');
  /* The same words markStale uses, because it is the same condition — and
     without them a disabled Export button on a freshly opened project has no
     explanation anywhere on screen. */
  else setChip('warn', 'Corners moved — lock the angle again');
  setShadowRes(d.shadowRes);
  setShadowSoft(d.shadowSoft);
  /* Not optional. Keyframed values are written INTO the objects rather than
     evaluated past them (see state/timeline.js's header), so a project whose
     objects were saved mid-scrub carries that frame's pose in o.pos. Putting
     the playhead back to zero and evaluating is what makes the track the
     source of truth again — without it a reopened project shows whatever
     frame its author last happened to be looking at. */
  /* A different scene: every decoder the old one held is now pointing at an
     object that no longer exists. Swept rather than released per-object,
     because a missed call site leaks a hardware decoder for the tab's life. */
  reapVideoReaders();
  snapKeysToFrames();        // keys saved between frames by older builds — see there
  applyAnimation(0);
  /* The timeline dock is rebuilt from events, never polled, and a project
     load changes everything it draws — which objects have tracks, where the
     keyframes are, the duration and the fps. Without this it kept showing
     the PREVIOUS project's lanes and its duration/fps fields, since nothing
     else it listens to fires here: goStage only toggles the dock's
     visibility, and the rails are a different rebuild entirely.

     state/history.js's applySnapshot emits the same event for the same
     reason after an undo. Loading a project is the larger version of that
     and was simply never told. */
  emit('anim:changed');
  resetHistory();
  rebuildHelpers();
  applyLighting(true);
  frameOrbit();
  goStage(S.solved ? 'compose' : 'calib');
  fitStage();
}

/** Start a clean, unsaved project — applyDoc itself, just fed a blank
 *  document instead of one opened from disk or the cloud. fromDoc({objects:[]})
 *  is already the whole trick: migrate() fills in every field from
 *  projectDefaults(), the same defaults S itself boots with, so this needs
 *  no separate "blank doc" shape to keep in sync by hand.
 *  Also detaches from whatever cloud project was open, the same reset
 *  deleteProject() does below — otherwise the next autosave would silently
 *  overwrite that project with this blank scene.
 *  applyDoc always sets S.seeded = true — right for opening a REAL project
 *  that was deliberately saved with zero objects (nothing should second-
 *  guess that), wrong here: ui/calibrate-view.js's seedRecess() only adds
 *  the starter recess when `!S.seeded`, so leaving it true silently skips
 *  it the next time manual setup or a photo upload runs, unlike a true
 *  fresh boot (state/store.js's own S.seeded starts false). Reset it back
 *  so New behaves exactly like a first-ever visit, not a project someone
 *  saved with nothing in it. */
export function newProject() {
  /* Leaving a shared project hands the turn back rather than making the next
     person wait out the expiry. Not awaited: starting a new project should not
     pause on a network call, and the expiry covers it if the call never lands. */
  if (openProject.id) releaseLock(openProject.id);
  stopWatching();
  Object.assign(turn, { shared: false, readOnly: false, heldBy: null, available: false });
  applyDoc(fromDoc({ objects: [] }));
  S.seeded = false;
  Object.assign(openProject, { id: null, title: 'Untitled', rev: 0, handedOver: false });
  emit('project:turn', { ...turn });
  emit('project:changed', openProject);
}

$('#btnNew').onclick = () => {
  if (S.objects.length && !confirm('Start a new project? Anything not saved will be lost.')) return;
  newProject();
};

/* ── local file ──────────────────────────────────────────────────────────── */

/** Download a self-contained .json — for sending a project to someone else,
 *  and the only way out of the browser where there's no folder to save into.
 *  Named after the project rather than a fixed string, so a folder of these
 *  is readable and two downloads don't collide as "…(1).json". */
export function saveProject() {
  const data = toDoc(S, embedPhoto, LEGACY_VERSION);
  const b = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(b);
  a.download = safeFileName(openProject.title || 'Untitled project');
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 4000);
  /* This one says "Project saved." out loud, so it has to count as one. A
     downloaded .json is the whole document — warning about unsaved work
     immediately after telling someone it was saved would just teach them to
     dismiss the prompt without reading it. */
  S.dirty = false;
  toast('Project saved.');
}

$('#fileProj').onchange = e => {
  const f = e.target.files[0];
  if (!f) return;
  const rd = new FileReader();
  rd.onload = async () => {
    try {
      const d = fromDoc(JSON.parse(rd.result));
      await registerDocFonts(d);      // before applyDoc: syncNode traces immediately
      applyDoc(d);
      toast('Project opened.');
      /* Not 'project:changed' — that also covers new/rename/duplicate, none
         of which should close a dialog that only opened this input to begin
         with. ui/account.js listens for this one specifically, once, right
         before it clicks #fileProj. */
      emit('project:opened-from-file');
    } catch (err) {
      console.error(err);
      toast('That file isn’t a readable Illusio 3D project.', true);
    }
  };
  rd.readAsText(f);
  e.target.value = '';
};

/* ── local library ───────────────────────────────────────────────────────────
   A named list of projects held on this machine — a folder the user picks if
   the browser can offer one, otherwise IndexedDB (services/projects-fs.js
   decides, and falls back on its own).

   This is what used to be "presets", and it is the same document either way:
   what toDoc() writes for a cloud project is exactly what gets stored here,
   which is why opening one is applyDoc() and not a second loader. The split
   into two named concepts was never a difference in the data, only in the
   UI wrapped around it — one had a library with rename/duplicate/overwrite,
   the other had autosave and a "currently open" row. Merged, projects have
   both, and this tier is the one that needs no account at all. */

/** @returns {Promise<object[]>} newest first, same ordering listProjects() uses. */
export const listLocalProjects = listLocal;
export const renameLocalProject = renameLocal;
export const duplicateLocalProject = duplicateLocal;
export const deleteLocalProject = deleteLocal;

/* Both clear S.dirty, which is the local half of what the cloud path has always
   done on a successful write. Only after the await: a failed save has not
   preserved anything, and telling the unload warning otherwise would drop the
   one prompt standing between a full-stop close and losing the work. */
export async function saveLocalProject(title) {
  const saved = await saveLocal(title, toDoc(S, true, LEGACY_VERSION));
  S.dirty = false;
  return saved;
}
export async function overwriteLocalProject(id) {
  const saved = await overwriteLocal(id, toDoc(S, true, LEGACY_VERSION));
  S.dirty = false;
  return saved;
}
/** Put a stored local project on screen. Same operation as opening a cloud
 *  one — only where the document came from differs. */
export async function openLocalProject(id) {
  const record = await getLocal(id);
  if (!record) throw new Error('That project no longer exists.');
  const doc = fromDoc(record.doc);
  await registerDocFonts(doc);      // before applyDoc: syncNode traces immediately
  applyDoc(doc);
  /* Deliberately does NOT become the open cloud project: this one lives on
     disk, and letting autosave adopt it would push a local file into whatever
     cloud row happened to be open. */
  Object.assign(openProject, { id: null, title: record.title || 'Untitled', rev: 0, handedOver: false });
  emit('project:changed', openProject);
  return record;
}
/** Copy a local project up to the account, leaving the local one alone. */
export async function promoteLocalToCloud(id, onProgress) {
  const record = await getLocal(id);
  if (!record) throw new Error('That project no longer exists.');
  return createProject(record.title, onProgress, record.doc);
}

/* ── coming back to where you were ───────────────────────────────────────────

   Autosave writes the open project to the cloud on every change, and nothing
   ever reopened it. So a reload — after a deploy, a crash, or a stray Ctrl-R —
   landed on an empty Calibrate with Design, Motion and Export greyed out,
   because those are gated on there BEING a scene and there wasn't one. The work
   was safe; it just took knowing to go and fetch it. Reported as losing access
   to the three stages after a refresh, which is exactly what it looks like from
   the outside.

   Only the id is kept. Everything else about the project is a server round trip
   away and would only be a second copy to go stale — and an id is not worth
   anything to anyone who cannot already read the row. */
const LAST_KEY = 'illusio3d.lastProject';

function rememberOpen() {
  try {
    if (openProject.id) localStorage.setItem(LAST_KEY, openProject.id);
    else localStorage.removeItem(LAST_KEY);
  } catch (_) { /* private mode: the restore is a convenience, not a promise */ }
}
/* Hung off the event the module already emits rather than written at each of
   the five places that assign openProject — new, open, create, delete, sign
   out. One of those would eventually be missed. */
on('project:changed', rememberOpen);

/**
 * Reopen whatever was open last, if anything, and if it is still ours to open.
 *
 * Deliberately quiet about the ordinary reasons it does nothing — no last
 * project, not signed in, no credentials in this build. It only speaks up when
 * there WAS one and it could not be had, because that is the case where
 * somebody is about to wonder where their work went.
 */
export async function restoreLastProject() {
  if (!isConfigured || !isSignedIn()) return false;
  let id = null;
  try { id = localStorage.getItem(LAST_KEY); } catch (_) {}
  if (!id) return false;

  /* Nothing here yet, or this would replace it. Restoring is a slow path — a
     session, then a row, then signed asset urls — and someone who started
     drawing while it was in flight must not have that thrown away underneath
     them. Checked at the last possible moment, immediately before applyDoc
     would wipe the scene. */
  if (S.solved || S.objects.length) return false;

  try {
    await loadProject(id);
    return true;
  } catch (err) {
    /* Deleted, or belonging to another account, or simply offline. Forget it
       either way: a stale id that fails every load is worse than none, and the
       list is one click away. */
    try { localStorage.removeItem(LAST_KEY); } catch (_) {}
    console.error('[illusio] could not reopen the last project', err);
    toast('Couldn’t reopen your last project — open it from Projects.', true);
    return false;
  }
}

/* ── importing objects from another project ─────────────────────────────────

   Opening a project replaces the scene. This takes PIECES out of one without
   disturbing what is on screen — the case being: a new installation, a
   different screen, and a logo or a model you already built once.

   Read without applying. fromDoc/hydrate are the same two steps loadProject
   takes; it is only applyDoc that is skipped, so an imported object has been
   through exactly the normalisation an opened one has. */
export async function readProjectDoc({ id, local }) {
  if (local) {
    const record = await getLocal(id);
    if (!record) throw new Error('That project no longer exists.');
    return fromDoc(record.doc);
  }
  const { data, error } = await requireClient()
    .from('projects').select('doc').eq('id', id).single();
  if (error) throw error;
  const doc = fromDoc(data.doc);
  hydrate(doc, await signedUrls(assetIdsIn(doc)));
  return doc;
}

/* Which objects a document offers, named the way the outliner names them. */
export function objectsIn(doc) {
  return (doc.objects || [])
    /* The camera is the viewer, not a prop — one per scene, and this one
       belongs to the project it came from. A recess or frame is fitted to ITS
       screen and would arrive wrong on any other. What is left is what someone
       actually means by "the objects I need". */
    .filter(o => o.type !== 'camera' && o.type !== 'recess' && o.type !== 'frame')
    .map(o => ({ id: o.id, type: o.type, name: o.name }));
}

/* Bytes, not references. An object arriving from a cloud project carries an
   assetId and a SIGNED url, and a signed url expires — so importing one into a
   project saved to disk would produce a scene that worked until it did not.
   Fetching the bytes and inlining them makes the import self-contained, which
   is exactly the state an object added by hand is in: the existing save path
   then uploads it (toV2) or keeps it inline, on its own terms. Deliberately
   not shared with the source project's asset row — the projects are separate,
   and deleting one must never empty the other. */
async function inlineAsset(holder, srcKey) {
  const url = holder[srcKey];
  if (!url || url.startsWith('data:')) return;
  const res = await fetch(url);
  if (!res.ok) throw new Error('One of those objects has a file that could not be read.');
  const blob = await res.blob();
  holder[srcKey] = await new Promise((ok, no) => {
    const r = new FileReader();
    r.onload = () => ok(r.result);
    r.onerror = () => no(new Error('That file could not be read.'));
    r.readAsDataURL(blob);
  });
  /* The id belonged to the OTHER project's asset row. Keeping it would make
     this document claim storage it does not own, and a save would link the two.
     The bytes are here now; a new row is written when this project is saved. */
  delete holder.assetId;
}

/**
 * Copy the chosen objects out of `doc` and into the scene on screen.
 *
 * Rescaled from that project's screen to this one, by the same rule the app
 * already applies when a screen size changes (scene/objects.js's
 * rescaleForScreen) — so an object keeps its PROPORTION of the screen and the
 * composition survives the move. Only the arrivals are rescaled; nothing
 * already here moves because something was pasted beside it.
 *
 * @returns {Promise<number>} how many arrived
 */
export async function importObjects(doc, ids, { withNulls = false } = {}) {
  const want = new Set(ids);
  const picked = (doc.objects || []).filter(o => want.has(o.id));
  if (!picked.length) return 0;

  /* A deep clone, so the source document is never mutated by the inlining
     below — it may still be held by the picker the user is standing in. */
  const arrivals = JSON.parse(JSON.stringify(picked));
  for (const o of arrivals) {
    if (o.params && (o.type === 'image' || o.type === 'model' || o.type === 'video'))
      await inlineAsset(o.params, 'src');
    if (o.map) await inlineAsset(o.map, 'src');
  }

  /* The faces first, and the whole source list rather than only the ones in
     use: registerDocFonts is what puts a traced family into document.fonts, and
     syncNode below traces immediately — a face registered after the fact leaves
     the glyphs already extruded in a fallback. */
  await registerDocFonts(doc);
  const have = new Set(S.fonts.map(f => f.family));
  for (const f of doc.fonts || []) if (!have.has(f.family)) S.fonts.push({ ...f });

  /* ids are per-project; the source's would collide — assigned in their own
     pass, before any syncNode runs, so a link between two arriving objects
     can be remapped to the new ids rather than silently pointing at ones
     that no longer mean anything here. A child whose master was not also
     selected has nothing to remap to and comes in unlinked instead — its
     numbers stay whatever they were relative to a master that didn't come
     along, which can read as "in the wrong place" for that one case, but a
     stale parentId pointing at nothing would be worse. */
  const idMap = new Map(arrivals.map(o => [o.id, S.nextId++]));
  arrivals.forEach(o => {
    o.id = idMap.get(o.id);
    if(o.parentId != null) o.parentId = idMap.has(o.parentId) ? idMap.get(o.parentId) : null;
    o.node = null;
    S.objects.push(o);
    syncNode(o);
  });
  arrivals.forEach(syncParenting);
  rescaleForScreen(doc.screen || S.screen, S.screen, arrivals);

  /* EACH ITS OWN NULL, when asked. Only the ones arriving at the top: a child
     that came in with its master already has something carrying it, and a null
     between the two would change what moves with what. */
  if (withNulls) {
    arrivals.forEach(syncTransform);
    for (const o of arrivals) if (o.parentId == null) parentToNewNull(o.id);
  }

  S.dirty = true;
  return arrivals.length;
}

/* THE INSET'S STYLE, brought across without its shape.

   objectsIn leaves the inset out because an inset IS the screen's opening: its
   width and height are worked out from the screen it sits in (refitRecess), so
   copying the numbers would be wrong on any other screen, and on a different
   aspect ratio it could not fit at all. But what makes one inset look like
   itself is not its opening — it is how deep it goes, how wide its border is,
   how rounded the lip is, and the material of the walls. Those carry over on
   any screen.

   So this applies them to THIS project's own inset, which then refits itself to
   this screen exactly as it does when the screen size changes. Sizes are
   rescaled by the ratio of the two screens' shorter sides — the same basis the
   defaults are scaled on (core/doc.js recessDefaults) — so the inset keeps its
   proportion of the screen: a border 12% of the height stays 12% of it. Only
   the source's own fitting is left behind (w, h, autoFill, and a corner
   screen's hinge), which is precisely the part that cannot travel. */
const INSET_SIZES = ['depth', 'border', 'bevel', 'borderDepth', 'borderT', 'borderR', 'borderB', 'borderL'];
const INSET_KEEPS = ['linkBorder', 'borderOverlay', 'bevelSegs'];
const INSET_LOOK = ['color', 'metalness', 'roughness', 'opacity', 'unlit', 'shadow'];

/** The inset a document offers, or null. */
export function insetIn(doc) {
  const r = (doc.objects || []).find(o => o.type === 'recess');
  return r ? { id: r.id, name: r.name } : null;
}

/**
 * Give this project's inset the depth, border, bevel and look of `doc`'s.
 * Adds an inset first if this project has none.
 *
 * @returns {boolean} whether there was an inset to take the style from
 */
export function importInsetStyle(doc) {
  const src = (doc.objects || []).find(o => o.type === 'recess');
  if (!src) return false;
  const rec = S.objects.find(o => o.type === 'recess') || addObject('recess');

  const from = doc.screen || S.screen;
  const om = Math.min(from.w, from.h), nm = Math.min(S.screen.w, S.screen.h);
  const k = om > 1e-6 ? nm / om : 1;
  const P = rec.params, sp = src.params || {};

  /* A corner screen's recess has no depth of its own: how far it reaches into
     each panel is that panel's width (see refitRecess), and P.depth is only a
     stale default there. Bringing it across would replace a real depth with a
     meaningless one, so it is left as this project has it. */
  const skip = sp.cornerHingeX != null ? ['depth'] : [];
  for (const key of INSET_SIZES) {
    if (skip.includes(key) || !(key in sp)) continue;
    // null means "follow the border" on the per-side fields and on borderDepth
    P[key] = sp[key] == null ? null : Number.isFinite(+sp[key]) ? +sp[key] * k : P[key];
  }
  for (const key of INSET_KEEPS) if (key in sp) P[key] = sp[key];
  for (const key of INSET_LOOK) if (key in src) rec[key] = src[key];

  refitRecess(rec);       // the opening follows THIS screen, whatever the source's was
  syncNode(rec);
  applyLook(rec);
  S.dirty = true;
  return true;
}

/**
 * Give this project the scene settings of `doc`: the ones in Scene Settings'
 * "Changes the export" panel — lighting preset, sun direction, shadow blur,
 * environment, occlusion, depth of field, and what is behind the content.
 *
 * Not the screen, the viewer, the timeline or the quality settings: those belong
 * to the installation and the delivery, which are this project's own. The depth
 * of field's focus comes only when it names a place (the screen, or a depth); a
 * focus that names an OBJECT points at an id in the other project, so it lands on
 * the screen here rather than on whatever happens to hold that number.
 */
export function importSceneSettings(doc) {
  if (doc.lighting) S.lighting = doc.lighting;
  S.lightAz = doc.lightAz ?? null;         // null: this preset's own key-light direction
  S.lightEl = doc.lightEl ?? null;
  if (doc.shadowSoft != null) setShadowSoft(doc.shadowSoft);
  if (doc.envInt != null) S.envInt = doc.envInt;
  if (doc.aoStrength != null) S.aoStrength = doc.aoStrength;
  if (doc.aoReach != null) S.aoReach = doc.aoReach;
  if (doc.dofStrength != null) S.dofStrength = doc.dofStrength;
  const f = doc.dofFocus;
  S.dofFocus = f && (f.mode === 'custom' || f.mode === 'screen')
    ? { mode: f.mode, z: f.z ?? 0, id: null } : { mode: 'screen', z: 0, id: null };
  if (doc.bg) S.bg = { ...S.bg, ...doc.bg };
  applyLighting(true);
  S.dirty = true;
  return true;
}

/**
 * Give this project the timeline length and frame rate of `doc`.
 *
 * Its own row in the import dialog rather than part of Scene settings: a length
 * shorter than this project's cuts off whatever is animated past it, and a new
 * frame rate moves every key onto the new rate's frames — both real edits to
 * the animation here, so they are asked for separately. Loop comes along, as the
 * third thing the timeline's toolbar holds. The keys themselves are not touched
 * beyond that snap; a key past the new end simply waits there, as it does when
 * the Duration field is shortened by hand.
 */
export function importTimeline(doc) {
  const tl = doc.timeline;
  if (!tl) return false;
  S.timeline.duration = tl.duration;
  S.timeline.fps = tl.fps;
  if (tl.loop != null) S.timeline.loop = tl.loop !== false;
  snapKeysToFrames();
  applyAnimation(Math.min(S.playhead, S.timeline.duration));
  emit('anim:changed');
  S.dirty = true;
  return true;
}

/* ── cloud ───────────────────────────────────────────────────────────────── */

/* The project currently open in the database, if any. `rev` is the version last
   seen; the save is refused if the row has moved on, which is what stops a
   second tab from being quietly overwritten. */
export const openProject = { id: null, title: 'Untitled', rev: 0, handedOver: false };

/* May THIS project be written to? The plain entitlement answer, except for a
   project that was handed over when its previous owner deleted their account
   (0028): that one stays writable whatever the new owner has bought, because
   they could edit it right up to the moment it became theirs and freezing it
   on arrival would half-undo the rescue. The policy says the same thing; this
   is here so the client does not refuse a save the database would have
   allowed. */
const cloudWritable = () => canSaveToCloud().ok || openProject.handedOver === true;

export const isCloudProject = () => !!openProject.id;

export async function listProjects() {
  if (!isConfigured || !isSignedIn()) return [];
  const { data, error } = await requireClient()
    .from('projects')
    /* owner_id and the lock columns so the list can say whose a project is and
       whether somebody is in it. The filtering is still the database's: this
       returns your own projects AND the ones shared with you, because the
       policy says so, not because of anything asked for here. */
    .select('id, title, rev, updated_at, thumb_asset_id, owner_id, locked_by, locked_by_email, lock_expires_at')
    .order('updated_at', { ascending: false });
  if (error) throw error;
  const me = currentUser()?.id;

  /* How many people each project is shared with, in one query for the whole
     list rather than one per row. The owner sees every share on their own
     projects and a collaborator sees only the row naming themselves, so this
     count means "people I can see" — which for the only reader of it, the
     owner looking at their own project, is the whole answer. */
  const shareCount = new Map();
  try {
    const { data: shares } = await requireClient()
      .from('project_shares')
      .select('project_id');
    for (const s of shares ?? []) shareCount.set(s.project_id, (shareCount.get(s.project_id) ?? 0) + 1);
  } catch { /* the list is still worth showing without the counts */ }

  return data.map((p) => ({
    ...p,
    mine: p.owner_id === me,
    sharedWith: shareCount.get(p.id) ?? 0,
    heldByOther: !!p.locked_by && p.locked_by !== me
      && !!p.lock_expires_at && new Date(p.lock_expires_at) > new Date()
  }));
}

/**
 * Load a project by id and put it on screen.
 *
 * One batched call signs every asset the document needs, rather than one request
 * per texture. The URLs go back into the document before it reaches applyDoc, so
 * nothing downstream — getTexture, buildGLBNode, the four live <img> elements —
 * had to learn what an asset id is.
 */
export async function loadProject(id) {
  /* Hand back the turn on the project being left, before taking one on the
     project being opened. Otherwise moving between two shared projects leaves
     the first locked until it expires, for no reason anyone could see. */
  if (openProject.id && openProject.id !== id) await releaseLock(openProject.id);

  const { data, error } = await requireClient()
    .from('projects').select('id, title, doc, rev, handed_over').eq('id', id).single();
  if (error) throw error;

  const doc = fromDoc(data.doc);
  const urls = await signedUrls(assetIdsIn(doc));
  hydrate(doc, urls);

  await registerDocFonts(doc);        // before applyDoc: syncNode traces immediately
  applyDoc(doc);
  Object.assign(openProject, { id: data.id, title: data.title, rev: data.rev,
                               handedOver: data.handed_over === true });
  S.dirty = false;
  await takeTurn(id);
  emit('project:changed', openProject);
  return openProject;
}

/* ── taking turns ────────────────────────────────────────────────────────────
   Only shared projects take turns. A project nobody else has is opened and
   saved exactly as it always was -- no lock is asked for, none is needed to
   save, and this whole mechanism stays invisible to someone working alone,
   which is almost everyone almost all of the time. */

export const turn = { shared: false, readOnly: false, heldBy: null, available: false };

async function takeTurn(id) {
  stopWatching();
  Object.assign(turn, { shared: false, readOnly: false, heldBy: null, available: false });
  try {
    if (!(await isShared(id))) return;
    turn.shared = true;
    const got = await claimLock(id);
    if (!got.ok) {
      turn.readOnly = true;
      turn.heldBy = got.by;
      watchForFree(id);
    }
  } catch (err) {
    /* Never leave someone stuck outside a project because the turn could not
       be worked out. Read-only is the safe side of that: they can look, and a
       save would be refused by the database anyway rather than silently lost. */
    console.error('[illusio] could not work out the editing turn', err);
    turn.shared = true;
    turn.readOnly = true;
    turn.heldBy = null;
  }
  emit('project:turn', { ...turn });
}

/* ── waiting for your turn ───────────────────────────────────────────────────
   Somebody looking at a project a colleague has should not have to guess when
   it is free, or reload to find out. The banner used to promise it "becomes
   editable again when they close it" and nothing whatsoever made that true.

   Polled rather than subscribed: the answer lives in one column of one row
   that changes a handful of times a day, and a poll needs no realtime
   publication, no second RLS surface and no socket to reconnect. Only while
   the tab is actually being looked at — a project left open on a second
   monitor at the end of the day should not spend the night asking. */
const WATCH_MS = 15000;
let watcher = null, watching = null, onVisible = null;

function stopWatching() {
  if (watcher) clearInterval(watcher);
  /* The visibilitychange handler has to come off with the interval. Each call
     to watchForFree adds one, and a session that waits on three projects in a
     row would otherwise be running the first two checks forever, against
     projects it no longer has open. */
  if (onVisible) removeEventListener('visibilitychange', onVisible);
  watcher = null;
  watching = null;
  onVisible = null;
}

function watchForFree(id) {
  stopWatching();
  watching = id;
  const look = async () => {
    if (watching !== id || openProject.id !== id) return stopWatching();
    if (document.visibilityState !== 'visible') return;
    let holder;
    try { holder = await lockHolder(id); } catch { return; }
    if (watching !== id || openProject.id !== id) return;

    if (holder && !holder.isMine) {
      /* Still theirs, but it may have changed hands while we waited. */
      if (holder.email !== turn.heldBy) { turn.heldBy = holder.email; emit('project:turn', { ...turn }); }
      return;
    }
    /* Free. NOT taken silently: whoever had it has been saving, so what is on
       this screen is behind. Claiming here and letting them carry on editing a
       stale document would end at a refused save and a puzzle. The offer is
       made instead, and takeOverProject() claims and reloads together. */
    stopWatching();
    turn.available = true;
    emit('project:turn', { ...turn });
  };
  watcher = setInterval(look, WATCH_MS);
  /* And immediately on coming back to the tab, rather than up to WATCH_MS
     later — returning to a window is exactly when somebody wants to know. */
  onVisible = look;
  addEventListener('visibilitychange', onVisible);
  look();
}

/** Take the project over once its holder has let go: claim the turn and reload,
 *  so the scene on screen is the one they left rather than the one we opened. */
export async function takeOverProject() {
  const id = openProject.id;
  if (!id) return { ok: false };
  const got = await claimLock(id);
  if (!got.ok) {
    /* Somebody was quicker. Back to waiting rather than pretending. */
    turn.available = false;
    turn.readOnly = true;
    turn.heldBy = got.by;
    watchForFree(id);
    emit('project:turn', { ...turn });
    toast(`${got.by || 'Someone else'} got there first.`, true);
    return { ok: false };
  }
  await loadProject(id);
  toast('You can edit this project now.');
  return { ok: true };
}

/* The other direction: this tab HAD the turn and lost it, because it slept
   past the expiry and somebody else picked the project up. Told as it happens
   rather than at the next save. */
on('share:turn-lost', async ({ projectId }) => {
  if (projectId !== openProject.id || !turn.shared) return;
  const holder = await lockHolder(projectId).catch(() => null);
  if (holder?.isMine) return;                 // reclaimed in the meantime
  turn.readOnly = true;
  turn.available = false;
  turn.heldBy = holder?.email ?? null;
  emit('project:turn', { ...turn });
  watchForFree(projectId);
});

/* The uploader toV2 hands each pending asset to.

   Two shapes arrive here (see toV2's own note): a `data:` URL for an image,
   a model or the photo, and — for a clip — nothing at all, because its bytes
   never entered the document and are sitting in local IndexedDB under
   holder.blobId instead. Resolving both here keeps toV2 pure: it decides
   WHAT needs uploading, this decides where the bytes come from.

   A blobId that resolves to nothing means the clip was added in a different
   browser and this one only ever saw the id. Failing loudly beats writing a
   cloud row that points at nothing. */
async function uploadRef(dataUrl, kind, holder) {
  if (dataUrl) return (await uploadDataUrl(dataUrl, kind)).id;
  const blob = await getBlob(holder.blobId);
  if (!blob)
    throw new Error('A video in this project isn’t stored on this device, so it can’t be uploaded. Open the project where it was added, or replace the clip.');
  return (await upload(blob, kind)).id;
}

/**
 * Create a cloud project from whatever is on screen.
 *
 * A scene built locally still has its blobs inline, so this is where a v1
 * document becomes a v2 one: each data URL is uploaded and replaced by an id.
 * That takes a moment when there is a model involved, hence the progress
 * callback — a dialog that sits there silently reads as a crash.
 */
export async function createProject(title = 'Untitled', onProgress, sourceDoc) {
  const client = requireClient();
  const user = currentUser();
  if (!user) throw new Error('Sign in to save to the cloud.');

  const allowed = canCreateProject();
  if (!allowed.ok) throw new Error(allowed.reason);

  /* Defaults to the live scene, but a caller can hand over a document it
     already has — that's how promoteLocalToCloud sends a LOCAL project up
     without first having to open it on screen. */
  const doc = sourceDoc ? JSON.parse(JSON.stringify(sourceDoc)) : toDoc(S, true, LEGACY_VERSION);
  await toV2(doc, uploadRef, onProgress);
  /* Explicitly, the way saveProject already does it. This used to happen by
     accident: toV2 deleted each src as it uploaded it, so what went into the
     row happened to be free of bytes. That delete was reaching into the LIVE
     SCENE — toDoc shares params and map — and blinding it, so it is gone, and
     the stripping it was doubling as has to be asked for. dehydrate works on
     copies, so the scene keeps what it is drawing from. */
  dehydrate(doc);
  doc.v = DOC_VERSION;

  const { data, error } = await client.from('projects')
    .insert({ owner_id: user.id, title, doc, doc_version: DOC_VERSION })
    .select('id, title, rev').single();
  if (error) throw error;

  await linkToProject(data.id, assetIdsIn(doc));
  /* Only the live scene becomes the open project. Copying a stored document
     up (promoteLocalToCloud) must NOT adopt it, or autosave would start
     pushing whatever is currently on screen into the row we just made for
     something else. */
  if (!sourceDoc) {
    // Just created, so not a handover whatever the previous project was.
    Object.assign(openProject, { id: data.id, title: data.title, rev: data.rev, handedOver: false });
    S.dirty = false;
    emit('project:changed', openProject);
  }
  return { id: data.id, title: data.title, rev: data.rev };
}

/**
 * Write the open project back.
 *
 * The update is guarded on the rev last seen. Zero rows back means another tab
 * saved in the meantime, and the honest thing is to say so rather than silently
 * discard whichever session lost the race.
 *
 * @returns {Promise<{ok:boolean, conflict?:boolean}>}
 */
export async function saveToCloud({ silent = false } = {}) {
  if (!openProject.id) return { ok: false };
  /* KEEPING IT IN THE CLOUD IS PAID FOR, and this is the same shape as the
     read-only refusal below and for the same reason: 0028's policy is what
     makes it true, and this is what stops a session that cannot write from
     uploading assets first and finding out afterwards. The project itself is
     untouched — it still opens and still exports. */
  if (!cloudWritable()) {
    const paid = canSaveToCloud();
    if (!silent) toast(paid.reason, true);
    return { ok: false, locked: true, reason: paid.reason };
  }
  /* Refused here as well as by the database. The database is what makes it
     true; this is what stops a read-only session uploading assets and burning
     a minute on a write that was never going to land. */
  if (turn.readOnly) {
    if (!silent) {
      toast(turn.heldBy
        ? `${turn.heldBy} is editing this project. Your changes are not being saved.`
        : 'Someone else is editing this project. Your changes are not being saved.', true);
    }
    return { ok: false, readOnly: true };
  }
  const client = requireClient();

  const doc = toDoc(S, true, LEGACY_VERSION);
  if (hasUnuploadedAssets(doc))
    await toV2(doc, uploadRef);
  dehydrate(doc);
  doc.v = DOC_VERSION;

  const { data, error } = await client.rpc('save_project', {
    p_id: openProject.id, p_doc: doc, p_rev: openProject.rev, p_title: openProject.title
  });
  if (error) throw error;

  if (data == null) {
    /* Two different failures arrive as the same empty answer, and they need
       different things said. A shared project that somebody else now holds is
       a turn that lapsed -- this tab slept past its expiry and another person
       picked the project up. An unshared one can only be the rev guard, which
       is the original case: the same person in a second tab. */
    if (turn.shared) {
      const holder = await lockHolder(openProject.id).catch(() => null);
      if (holder && !holder.isMine) {
        turn.readOnly = true;
        turn.heldBy = holder.email;
        emit('project:turn', { ...turn });
        toast(`${holder.email || 'Someone else'} has taken over editing this project. Your changes since then are not saved.`, true);
        return { ok: false, lost: true };
      }
    }
    toast('This project was changed in another tab. Reload it before saving, or your work here will replace theirs.', true);
    return { ok: false, conflict: true };
  }

  openProject.rev = data;
  await linkToProject(openProject.id, assetIdsIn(doc));
  S.dirty = false;
  emit('project:saved', openProject);
  if (!silent) toast('Saved.');
  return { ok: true };
}

export async function renameProject(title) {
  if (!openProject.id) return;
  openProject.title = title;
  await saveToCloud({ silent: true });
}

/* Rename any cloud project, open or not — what the list's inline name field
   needs. A plain UPDATE of the title rather than the save_project RPC: that
   RPC exists to write a whole document under an optimistic-concurrency rev
   check, and rebuilding a rename around it would mean fetching the doc just
   to send it back unchanged. A title carries no such race — last write wins
   is the right answer for it — and projects_own is `for all`, so RLS covers
   this the same way. */
export async function renameProjectById(id, title) {
  const { error } = await requireClient().from('projects').update({ title }).eq('id', id);
  if (error) throw error;
  if (openProject.id === id) {
    openProject.title = title;
    emit('project:changed', openProject);
  }
}

export async function deleteProject(id) {
  const { error } = await requireClient().from('projects').delete().eq('id', id);
  if (error) throw error;
  if (openProject.id === id) {
    Object.assign(openProject, { id: null, title: 'Untitled', rev: 0, handedOver: false });
    emit('project:changed', openProject);
  }
}

/* ── autosave ────────────────────────────────────────────────────────────────
   pushHistory is the right trigger: it already fires exactly when something
   worth remembering happened, and it already dedupes by comparing serialised
   snapshots, so a no-op edit does not reach the network. Two seconds is long
   enough that dragging a gizmo is one save rather than fifty. */

const AUTOSAVE_MS = 2000;
let timer = null, inFlight = false, again = false;

export function scheduleAutosave() {
  if (!openProject.id || !isSignedIn()) return;
  /* Same reasoning as the turn check below — a timer that fires every two
     seconds into a refusal is a toast storm, and the answer will not have
     changed by the next tick. The Projects list is where this is explained;
     see ui/account.js. */
  if (!cloudWritable()) return;
  /* Nothing to schedule while somebody else has the turn. Without this the
     banner would say "not being saved" while a timer tried every two seconds
     to save anyway, and the toast from each refusal would bury the app. */
  if (turn.readOnly) return;
  clearTimeout(timer);
  timer = setTimeout(runAutosave, AUTOSAVE_MS);
}

async function runAutosave() {
  if (inFlight) { again = true; return; }   // coalesce; do not queue a second write
  inFlight = true;
  try {
    await saveToCloud({ silent: true });
  } catch (err) {
    console.error('[illusio] autosave failed', err);
    toast('Couldn’t save to the cloud. Your work is still here — check your connection.', true);
  } finally {
    inFlight = false;
    if (again) { again = false; scheduleAutosave(); }
  }
}

/* Two different questions, and they used to share one line. "Is there work
   someone would be upset to lose" is S.dirty; "should this go to the cloud" is
   scheduleAutosave, which returns early unless a cloud project is open. Setting
   the flag INSIDE that guard meant local work never became dirty — so the only
   projects that got an unload warning were the ones already being saved to a
   server every two seconds, and the ones with no copy anywhere but this tab got
   nothing. Marking is now unconditional; writing is still not. */
on('history:pushed', () => {
  S.dirty = true;
  scheduleAutosave();
});

/* A save in flight when the tab closes would be lost. This does not block the
   close — nothing may — but it does mark the work as unsaved so the browser can
   warn, which is the only honest thing available here.
   No openProject.id test: that is what limited this to cloud projects. */
addEventListener('beforeunload', ev => {
  if (S.dirty) { ev.preventDefault(); ev.returnValue = ''; }
});
